import { useCallback, useEffect, useRef, useState } from "react";

const defaultOptions = {
  immediate: true,
  deps: [],
};

const resolveData = (response) => {
  if (response && Object.prototype.hasOwnProperty.call(response, "data")) {
    return response.data;
  }
  return response;
};

export default function useApi(apiFn, options = defaultOptions) {
  const { immediate = true, deps = [] } = options;
  const [data, setData] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const execute = useCallback(
    async (...args) => {
      setLoading(true);
      setError(null);
      try {
        const response = await apiFn(...args);
        const resolved = resolveData(response);
        if (isMountedRef.current) {
          setData(resolved);
        }
        return resolved;
      } catch (err) {
        if (isMountedRef.current) {
          setError(err);
        }
        throw err;
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiFn, ...deps],
  );

  useEffect(() => {
    if (immediate) {
      execute();
    }
  }, [execute, immediate]);

  return {
    data,
    setData,
    loading,
    error,
    execute,
  };
}
